import { Button } from "@amzn/awsui-components-react";
import * as React from "react";
import { useState } from "react";
import { AGENT_CHAT_PANEL_COLLAPSED_WIDTH_PX } from "../../utils/agent-chat-utils";
import "../../styles/component/layout/PageBaseLayoutV2.scss";

const RIGHT_SECTION_ID = "pf-page-toolkit";

const isChatPanelOpen = (): boolean => {
    const chatPanelWidth = parseFloat(document.documentElement.style.getPropertyValue("--qa-panel-width"));
    return chatPanelWidth > AGENT_CHAT_PANEL_COLLAPSED_WIDTH_PX;
};

/**
 * Live page layout for the PathFinder Agentic UI lab: main content plus a fixed-width
 * Toolkit column. Opening the Quick agent chat panel collapses an open Toolkit, and closing
 * the chat re-opens it only if the chat was what collapsed it; a manual toggle always wins.
 * The chat panel is only observed (via the
 * --qa-panel-width variable it already publishes), never changed.
 */
export const PageBaseLayoutV2 = (props: {
    mainContent: JSX.Element;
    rightContent: JSX.Element;
    /** Shows the Toolkit collapse button and the chat-panel auto-collapse (live page only) */
    expandable?: boolean;
    hideRightSection?: boolean;
}) => {
    const [rightSectionExpanded, setRightSectionExpandedState] = useState<boolean>(() => !props.expandable || !isChatPanelOpen());
    const rightSectionExpandedRef = React.useRef(rightSectionExpanded);
    // True only while the Toolkit is closed because the chat panel opened, never after a manual toggle
    const autoCollapsedRef = React.useRef(!rightSectionExpanded);
    // Chat-driven toggles snap with the chat panel; only a manual toggle animates
    const [isChatDrivenToggle, setIsChatDrivenToggle] = useState<boolean>(false);

    const setRightSectionExpanded = (expanded: boolean) => {
        rightSectionExpandedRef.current = expanded;
        setRightSectionExpandedState(expanded);
    };

    React.useEffect(() => {
        if (!props.expandable || props.hideRightSection) return;
        let wasChatOpen = isChatPanelOpen();
        const observer = new MutationObserver(() => {
            const isChatOpen = isChatPanelOpen();
            if (isChatOpen === wasChatOpen) return;
            wasChatOpen = isChatOpen;
            if (isChatOpen && rightSectionExpandedRef.current) {
                autoCollapsedRef.current = true;
                setIsChatDrivenToggle(true);
                setRightSectionExpanded(false);
            } else if (!isChatOpen && autoCollapsedRef.current) {
                autoCollapsedRef.current = false;
                setIsChatDrivenToggle(true);
                setRightSectionExpanded(true);
            }
        });
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ["style"] });
        return () => observer.disconnect();
    }, [props.expandable, props.hideRightSection]);

    if (props.hideRightSection) {
        return (
            <div className="page-layout-v2">
                <div className="page-layout-main page-layout-v2-main">
                    {props.mainContent}
                </div>
            </div>
        );
    }

    return (
        <div className={`page-layout-v2${rightSectionExpanded ? "" : " page-layout-v2--right-collapsed"}${isChatDrivenToggle ? " page-layout-v2--instant" : ""}`}>
            <div className="page-layout-main page-layout-v2-main">
                {props.mainContent}
            </div>
            {props.expandable && <div className="page-layout-v2-toggle">
                <Button
                    variant="icon"
                    iconName={rightSectionExpanded ? "angle-right-double" : "angle-left-double"}
                    ariaLabel={rightSectionExpanded ? "Collapse toolkit" : "Expand toolkit"}
                    ariaExpanded={rightSectionExpanded}
                    ariaControls={RIGHT_SECTION_ID}
                    onClick={() => {
                        autoCollapsedRef.current = false;
                        setIsChatDrivenToggle(false);
                        setRightSectionExpanded(!rightSectionExpanded);
                    }}
                />
            </div>}
            <aside id={RIGHT_SECTION_ID} aria-label="Toolkit" className="page-layout-v2-right">
                {props.rightContent}
            </aside>
        </div>
    );
};
